import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  AgendaCanalContato,
  AgendaClassificacao,
  AgendaContexto,
  AgendaExame,
  AgendaGradeLinha,
  AgendaOpcao,
  AgendaPlanoPaciente,
  AgendaPrecoPaciente,
  AgendaPreparoExame,
  AgendaRetornoDisponivel,
} from '../../../../interfaces/agenda';
import { AgendaModaisService } from '../../../../services/agenda/agenda-modais.service';
import { AgendaService, mensagemErroAgenda } from '../../../../services/agenda/agenda.service';
import { AgendaFiltroAplicado } from '../agenda-filtro';
import { AgendaPacienteComponent } from '../agenda-paciente/agenda-paciente.component';
import { AgendaPacienteEscolhido } from '../agenda-paciente/agenda-paciente';
import { AgendaCuponsComponent } from '../agenda-cupons/agenda-cupons.component';
import {
  AgendaModoAgendamento,
  AgendaTipoAgendamento,
  CategoriasPermitidas,
  CONVENIO_CARTAO,
  DURACAO_ENCAIXE_PADRAO,
  EspecialidadeAgenda,
  HORA_ENCAIXE_PADRAO,
  OpcaoClassif,
  TextoPreco,
  camposFaltando,
  categoriaInicial,
  categoriasPermitidas,
  classifInicial,
  classifPadrao,
  convenioInicial,
  corpoEncaixe,
  corpoNovo,
  ehRetorno,
  especialidadeDaAgenda,
  especialidadeEfetiva,
  exibicaoConvenio,
  ExibicaoConvenio,
  opcoesClassifModal,
  podeEncaixar,
  textoPreco,
} from './agenda-agendamento';

const ESPERA_BUSCA_EXAME_MS = 600;
const MINIMO_BUSCA = 3;

/**
 * Novo agendamento num horário livre (modo "novo", com a linha da grade) ou encaixe fora da grade
 * na agenda do filtro (modo "encaixe"). Grava e devolve a mensagem do back em `salvo`.
 */
@Component({
  selector: 'app-agenda-agendamento',
  standalone: true,
  imports: [CommonModule, FormsModule, AgendaPacienteComponent, AgendaCuponsComponent],
  templateUrl: './agenda-agendamento.component.html',
  styleUrl: './agenda-agendamento.component.scss',
})
export class AgendaAgendamentoComponent implements OnInit, OnDestroy {
  @Input() modo: AgendaModoAgendamento = 'novo';
  /** Horário livre escolhido (modo novo). */
  @Input() linha: AgendaGradeLinha | null = null;
  @Input({ required: true }) filtro!: AgendaFiltroAplicado;
  @Input() contexto: AgendaContexto | null = null;
  @Input() classificacoes: AgendaClassificacao[] = [];
  /** Exame já escolhido fora do modal (Encontrar disponibilidade). */
  @Input() exameInicial: { nrSeqProcInterno: number; cdProcedimento: number | null; dsProcExame: string } | null = null;
  /** Paciente já escolhido no Encontrar disponibilidade. */
  @Input() pacienteInicial: AgendaPacienteEscolhido | null = null;

  @Output() fechar = new EventEmitter<void>();
  @Output() salvo = new EventEmitter<string>();

  readonly duracoes = [10, 15, 20, 30, 40, 60];
  readonly CONVENIO_CARTAO = CONVENIO_CARTAO;

  canais: AgendaCanalContato[] = [];
  convenios: AgendaOpcao[] = [];
  conveniosCarregados = false;
  categorias: AgendaOpcao[] = [];
  carregandoCategorias = false;
  especialidade: EspecialidadeAgenda | null = null;
  especialidadePendente = false;
  retornos: Record<string, AgendaRetornoDisponivel | null> = {};

  paciente: AgendaPacienteEscolhido | null = null;
  plano: AgendaPlanoPaciente | null = null;

  cdConvenio: number | null = null;
  cdCategoria: string | null = null;
  cdCanal: number | null = null;
  ieClassifAgenda = '';
  dsObservacao = '';
  hrInicio = HORA_ENCAIXE_PADRAO;
  nrMinutoDuracao: number | null = DURACAO_ENCAIXE_PADRAO;

  buscaExame = '';
  exames: AgendaExame[] = [];
  buscandoExame = false;
  vazioExame = '';
  exame: { nrSeqProcInterno: number; cdProcedimento: number | null; dsProcExame: string } | null = null;
  preparo: AgendaPreparoExame | null = null;

  preco: AgendaPrecoPaciente | null = null;
  carregandoPreco = false;
  duplicidadeExame: string | null = null;

  salvando = false;
  erro = '';

  private convManual = false;
  private classifManual = false;
  private fecharRegistro: (() => void) | null = null;
  private timerExame: ReturnType<typeof setTimeout> | null = null;
  // O plano pode chegar antes dos convênios; a pré-seleção espera a lista.
  private conveniosProntos: Promise<void> = Promise.resolve();
  // Respostas atrasadas (categorias, preço...) não podem sobrescrever a escolha atual.
  private seq = { paciente: 0, categorias: 0, preco: 0, exame: 0, preparo: 0, dup: 0 };

  constructor(private api: AgendaService, private modais: AgendaModaisService) {}

  async ngOnInit() {
    this.fecharRegistro = this.modais.abrir();
    if (this.modo === 'encaixe') this.dsObservacao = 'ENCAIXE';
    this.ieClassifAgenda = classifPadrao(this.tipo);
    if (this.ehExame && this.exameInicial) {
      this.exame = { ...this.exameInicial };
      void this.carregarPreparo(this.exameInicial.nrSeqProcInterno);
    }
    this.conveniosProntos = this.carregarConvenios();
    await Promise.all([this.carregarCanais(), this.conveniosProntos, this.carregarEspecialidades(), this.carregarRetornos()]);
    this.aplicarClassifInicial();
  }

  ngOnDestroy() {
    if (this.timerExame) clearTimeout(this.timerExame);
    for (const k of Object.keys(this.seq) as (keyof typeof this.seq)[]) this.seq[k]++;
    this.fecharRegistro?.();
    this.fecharRegistro = null;
  }

  @HostListener('document:keydown.escape')
  aoEsc() {
    if (this.modais.coberto()) return;
    this.cancelar();
  }

  // --- Derivados ----------------------------------------------------------------------------

  get tipo(): AgendaTipoAgendamento {
    const t = this.modo === 'novo' ? this.linha?.tipo : this.filtro?.tipo;
    return t === 'EXAME' ? 'EXAME' : 'CONSULTA';
  }

  get ehExame(): boolean { return this.tipo === 'EXAME'; }
  get supervisor(): boolean { return !!this.contexto?.supervisor; }
  get encaixe(): boolean { return this.modo === 'encaixe'; }

  get cdAgenda(): number | null {
    return (this.modo === 'novo' ? this.linha?.cdAgenda : this.filtro?.cdAgenda) || null;
  }

  get data(): string {
    const d = this.linha?.dtAgenda?.slice(0, 10);
    return this.modo === 'novo' && d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : this.filtro.data;
  }

  get dataBr(): string {
    const [a, m, d] = this.data.split('-');
    return `${d}/${m}/${a}`;
  }

  get cdEstabelecimento(): number | null {
    return this.linha?.estabelecimento?.cdEstabelecimento || this.filtro?.cdEstabelecimento || null;
  }

  get titulo(): string { return this.encaixe ? 'Encaixe' : 'Novo agendamento'; }

  get subtitulo(): string {
    const ag = this.linha?.dsAgenda || this.filtro?.agenda?.dsAgenda || '';
    const hora = !this.encaixe && this.linha ? ` · ${this.linha.hrInicio ?? ''} (${this.linha.nrMinutoDuracao ?? 0} min)` : '';
    return `${ag}${ag ? ' · ' : ''}${this.dataBr}${hora}`;
  }

  get nomeMedico(): string {
    const l = this.linha;
    return l?.medico?.nmMedico || l?.medicoAgenda?.nmMedico || l?.dsAgenda || this.filtro?.agenda?.dsAgenda || '';
  }

  get nomeUnidade(): string { return this.linha?.estabelecimento?.nmEstabelecimento || ''; }

  get dataHoraResumo(): string {
    if (this.encaixe) return `${this.dataBr} ${this.hrInicio || ''} (${this.nrMinutoDuracao ?? 0} min)`.trim();
    return `${this.dataBr} ${this.linha?.hrInicio ?? ''} (${this.linha?.nrMinutoDuracao ?? 0} min)`;
  }

  get itemResumo(): string {
    if (this.ehExame) return this.exame?.dsProcExame ?? '';
    const cd = this.cdEspecialidadeEfetiva;
    const daAgenda = this.especialidade?.opcoes.find(o => o.cdEspecialidade === cd)?.dsEspecialidade;
    return daAgenda || (cd && cd === this.linha?.especialidade?.cdEspecialidade ? this.linha.especialidade.dsEspecialidade ?? '' : '');
  }

  get convenioResumo(): ExibicaoConvenio {
    const conv = this.convenios.find(c => c.codigo === this.cdConvenio)?.descricao ?? '';
    const cat = this.categorias.find(c => String(c.codigo) === this.cdCategoria)?.descricao ?? '';
    return exibicaoConvenio(this.cdConvenio, conv, cat);
  }

  get opcoesClassif(): OpcaoClassif[] {
    return opcoesClassifModal(this.tipo, this.classificacoes, podeEncaixar(this.contexto), this.retornos, this.supervisor);
  }

  get classifSelecionada(): OpcaoClassif | null {
    return this.opcoesClassif.find(o => o.valor === this.ieClassifAgenda) ?? null;
  }

  /** Retorno esgotado escolhido por supervisão: passa, mas fica dito. */
  get avisoRetorno(): string | null {
    if (!ehRetorno(this.ieClassifAgenda)) return null;
    const r = this.retornos[this.ieClassifAgenda];
    if (!r || r.permitido) return null;
    return r.dsMensagem || 'Limite de retornos atingido para esta agenda.';
  }

  get permitidas(): CategoriasPermitidas {
    return categoriasPermitidas(this.cdConvenio, this.categorias, !!this.paciente, this.plano);
  }

  get aviso224(): string | null {
    if (this.cdConvenio !== CONVENIO_CARTAO || !this.paciente) return null;
    return this.permitidas.bloqueio;
  }

  get cdEspecialidadeEfetiva(): number | null {
    if (this.ehExame) return null;
    return especialidadeEfetiva(this.especialidade, this.linha?.especialidade?.cdEspecialidade, this.filtro?.cdEspecialidade);
  }

  get faltando(): string[] {
    return camposFaltando({
      modo: this.modo,
      tipo: this.tipo,
      cdPessoaFisica: this.paciente?.cdPessoaFisica ?? null,
      cdConvenio: this.cdConvenio,
      cdCategoria: this.cdCategoria,
      categoriaBloqueada: !!this.permitidas.bloqueio,
      cdCanal: this.cdCanal,
      especialidadeMista: !!this.especialidade?.mista,
      cdEspecialidade: this.cdEspecialidadeEfetiva,
      especialidadePendente: this.especialidadePendente,
      nrSeqProcInterno: this.exame?.nrSeqProcInterno ?? null,
      ieClassifAgenda: this.ieClassifAgenda,
      classifDesabilitada: !!this.classifSelecionada?.desabilitada,
      duplicado: !!this.duplicidadeExame,
      hrInicio: this.hrInicio,
      nrMinutoDuracao: this.nrMinutoDuracao,
    });
  }

  get podeSalvar(): boolean { return !this.salvando && !this.faltando.length; }

  get precoInfo(): TextoPreco | null {
    return this.preco ? textoPreco(this.preco, this.cdConvenio, this.plano) : null;
  }

  /** O que ainda falta para consultar o preço; null = já dá. */
  get faltaParaPreco(): string | null {
    if (!this.paciente) return 'Escolha o paciente para ver o preço.';
    if (this.ehExame ? !this.exame : !this.cdEspecialidadeEfetiva) {
      return `Escolha ${this.ehExame ? 'o exame' : 'a especialidade'} para ver o preço.`;
    }
    if (!this.cdConvenio || !this.cdCategoria || this.permitidas.bloqueio) return 'Selecione o convênio e a categoria.';
    return null;
  }

  // --- Cargas de apoio ----------------------------------------------------------------------

  private async carregarCanais() {
    try {
      this.canais = await this.api.canaisContato();
    } catch {
      this.canais = [];
      this.erro = 'Não foi possível carregar os canais de contato.';
    }
  }

  private async carregarConvenios() {
    try {
      this.convenios = await this.api.convenios(this.cdEstabelecimento, this.cdAgenda);
      this.conveniosCarregados = true;
    } catch (e) {
      this.convenios = [];
      this.erro = mensagemErroAgenda(e, 'Não foi possível carregar os convênios.');
    }
  }

  private async carregarEspecialidades() {
    if (this.ehExame || !this.cdAgenda) return;
    this.especialidadePendente = true;
    try {
      const lista = await this.api.especialidadesDaAgenda(this.cdAgenda);
      this.especialidade = especialidadeDaAgenda(lista, this.filtro?.cdEspecialidade ?? null);
    } catch {
      // Sem a lista não dá para saber se é mista: o back resolve a especialidade da agenda.
      this.especialidade = null;
    } finally {
      this.especialidadePendente = false;
    }
  }

  /** Opções de retorno esgotadas na agenda e dia. Falha não trava: a gravação revalida. */
  private async carregarRetornos() {
    if (this.ehExame || !this.cdAgenda) return;
    const cdAgenda = this.cdAgenda;
    // O lote só responde por 'R'; o R13 vai na rota avulsa.
    const [lote, online] = await Promise.all([
      this.api.retornoDisponivelLote([{ cdAgenda, dtAgenda: this.data }]).catch(() => [] as AgendaRetornoDisponivel[]),
      this.api.retornoDisponivel(cdAgenda, this.data, 'R13').catch(() => null),
    ]);
    this.retornos = {
      R: lote.find(r => r.cdAgenda === cdAgenda) ?? lote[0] ?? null,
      R13: online,
    };
  }

  private aplicarClassifInicial() {
    if (this.classifManual) return;
    this.ieClassifAgenda = classifInicial(this.opcoesClassif, this.linha?.ieClassifAgenda, classifPadrao(this.tipo));
  }

  // --- Paciente (escolhido no app-agenda-paciente) --------------------------------------------

  aoMudarPaciente(p: AgendaPacienteEscolhido | null) {
    this.seq.paciente++;
    this.paciente = p;
    this.plano = null;
    this.convManual = false;
    this.cdConvenio = null;
    this.cdCategoria = null;
    this.categorias = [];
    this.preco = null;
    this.duplicidadeExame = null;
    this.seq.preco++;
    this.seq.dup++;
    if (p) void this.verificarDuplicidade();
  }

  /** Com o plano (mesma fonte da trava do 224) pré-seleciona convênio e categoria. */
  async aoCarregarPlano(plano: AgendaPlanoPaciente | null) {
    const seq = this.seq.paciente;
    this.plano = plano;
    await this.conveniosProntos;
    if (seq !== this.seq.paciente || this.convManual || !this.paciente) return;
    this.cdConvenio = convenioInicial(this.convenios, plano);
    await this.carregarCategorias();
    if (seq !== this.seq.paciente || this.convManual) return;
    this.cdCategoria = this.permitidas.forcada ?? categoriaInicial(this.cdConvenio, this.permitidas.lista);
    await this.atualizarPreco();
  }

  // --- Convênio e categoria -----------------------------------------------------------------

  async trocarConvenio(cd: number | null) {
    this.convManual = true;
    this.cdConvenio = cd;
    this.cdCategoria = null;
    this.preco = null;
    await this.carregarCategorias();
    this.cdCategoria = this.permitidas.forcada ?? (this.permitidas.lista.length === 1 ? String(this.permitidas.lista[0].codigo) : null);
    await this.atualizarPreco();
  }

  async trocarCategoria(cd: string | null) {
    this.cdCategoria = cd;
    await this.atualizarPreco();
  }

  private async carregarCategorias() {
    const seq = ++this.seq.categorias;
    if (!this.cdConvenio) { this.categorias = []; return; }
    this.carregandoCategorias = true;
    try {
      const lista = await this.api.categorias(this.cdConvenio, this.cdAgenda);
      if (seq === this.seq.categorias) this.categorias = lista;
    } catch {
      if (seq === this.seq.categorias) this.categorias = [];
    } finally {
      if (seq === this.seq.categorias) this.carregandoCategorias = false;
    }
  }

  // --- Especialidade, classificação e exame --------------------------------------------------

  async trocarEspecialidade(cd: number | null) {
    if (!this.especialidade) return;
    this.especialidade = { ...this.especialidade, escolhida: cd };
    await this.atualizarPreco();
  }

  trocarClassif(v: string) {
    this.classifManual = true;
    this.ieClassifAgenda = v;
  }

  aoDigitarExame() {
    if (this.timerExame) clearTimeout(this.timerExame);
    const q = this.buscaExame.trim();
    const seq = ++this.seq.exame;
    if (q.length < MINIMO_BUSCA) {
      this.exames = [];
      this.vazioExame = '';
      this.buscandoExame = false;
      return;
    }
    this.timerExame = setTimeout(async () => {
      this.buscandoExame = true;
      try {
        const lista = await this.api.exames(q, this.cdAgenda);
        if (seq !== this.seq.exame) return;
        // No ramo livre (agenda sem regra) as linhas não têm agenda: filtrar zeraria a lista.
        const livre = lista[0]?.ieProcLivre === 'S';
        const daAgenda = this.cdAgenda && !livre ? lista.filter(e => e.cdAgenda === this.cdAgenda) : lista;
        this.exames = daAgenda;
        this.vazioExame = daAgenda.length ? '' : `Nenhum exame encontrado${lista.length ? ' nesta agenda' : ''}.`;
      } catch {
        if (seq === this.seq.exame) { this.exames = []; this.vazioExame = 'Erro ao buscar exames.'; }
      } finally {
        if (seq === this.seq.exame) this.buscandoExame = false;
      }
    }, ESPERA_BUSCA_EXAME_MS);
  }

  async escolherExame(e: AgendaExame) {
    this.exame = { nrSeqProcInterno: e.nrSeqProcInterno, cdProcedimento: e.cdProcedimento, dsProcExame: e.dsProcExame ?? '' };
    this.buscaExame = '';
    this.exames = [];
    this.vazioExame = '';
    await Promise.all([this.carregarPreparo(e.nrSeqProcInterno), this.atualizarPreco(), this.verificarDuplicidade()]);
  }

  removerExame() {
    this.exame = null;
    this.preparo = null;
    this.preco = null;
    this.duplicidadeExame = null;
    this.seq.preparo++;
    this.seq.preco++;
    this.seq.dup++;
  }

  private async carregarPreparo(nrSeq: number) {
    const seq = ++this.seq.preparo;
    this.preparo = null;
    try {
      const p = await this.api.preparoExame(nrSeq);
      if (seq === this.seq.preparo) this.preparo = p && (p.pac || p.usuario) ? p : null;
    } catch {
      /* preparo é informativo */
    }
  }

  // --- Preço e duplicidade -------------------------------------------------------------------

  async atualizarPreco() {
    const seq = ++this.seq.preco;
    if (this.faltaParaPreco || !this.paciente) { this.preco = null; this.carregandoPreco = false; return; }
    this.carregandoPreco = true;
    try {
      const p = await this.api.precoPaciente({
        tipo: this.tipo,
        cdPessoaFisica: this.paciente.cdPessoaFisica,
        cdEstabelecimento: this.cdEstabelecimento,
        cdConvenio: this.cdConvenio,
        cdCategoria: this.cdCategoria,
        // Consulta vai só pela especialidade: o procedimento do horário é o da titular da agenda.
        cdEspecialidade: this.ehExame ? null : this.cdEspecialidadeEfetiva,
        nrSeqProcInterno: this.exame?.nrSeqProcInterno ?? null,
        cdProcedimento: this.exame?.cdProcedimento ?? null,
      });
      if (seq === this.seq.preco) this.preco = p ?? { vlPlanoPaciente: null, cdConvenioPaciente: null, cdCategoriaPaciente: null, vlParticular: null, planId: null, planName: null, planModality: null };
    } catch {
      if (seq === this.seq.preco) this.preco = null;
    } finally {
      if (seq === this.seq.preco) this.carregandoPreco = false;
    }
  }

  /**
   * O mesmo exame no mesmo dia trava o salvar, como no original. Só no horário livre: no encaixe a
   * especialidade da agenda de exame não é conhecida (a gravação recusa). Consulta fica com a
   * trigger do Tasy. Falha da consulta não trava.
   */
  private async verificarDuplicidade() {
    const seq = ++this.seq.dup;
    const esp = this.linha?.especialidade?.cdEspecialidade;
    if (!this.ehExame || this.encaixe || !this.paciente || !this.exame || !esp) { this.duplicidadeExame = null; return; }
    try {
      const d = await this.api.duplicidade({
        cdPessoaFisica: this.paciente.cdPessoaFisica,
        data: this.data,
        cdEspecialidade: esp,
        nrSeqProcInterno: this.exame.nrSeqProcInterno,
      });
      if (seq !== this.seq.dup) return;
      this.duplicidadeExame = d?.temDuplicidade
        ? `Paciente já tem este exame neste dia (${d.dsAgendaExistente ?? ''} - ${d.hrExistente ?? ''}). Não é permitido agendar o mesmo exame duas vezes no mesmo dia.`
        : null;
    } catch {
      if (seq === this.seq.dup) this.duplicidadeExame = null;
    }
  }

  // --- Gravação ------------------------------------------------------------------------------

  async salvar() {
    if (this.salvando || this.faltando.length || !this.paciente || !this.cdAgenda) return;
    this.salvando = true;
    this.erro = '';
    try {
      const comum = {
        tipo: this.tipo,
        cdPessoaFisica: this.paciente.cdPessoaFisica,
        cdConvenio: this.cdConvenio!,
        cdCategoria: this.cdCategoria!,
        ieClassifAgenda: this.ieClassifAgenda,
        cdCanal: this.cdCanal!,
        dsObservacao: this.dsObservacao,
        cdEspecialidade: this.cdEspecialidadeEfetiva,
        nrSeqProcInterno: this.exame?.nrSeqProcInterno ?? null,
      };
      const r = this.encaixe
        ? await this.api.criarEncaixe(corpoEncaixe({
          ...comum, cdAgenda: this.cdAgenda, dtAgenda: this.data, hrInicio: this.hrInicio, nrMinutoDuracao: this.nrMinutoDuracao!,
        }))
        : await this.api.criarAgendamento(corpoNovo({
          ...comum,
          cdAgenda: this.cdAgenda,
          nrSequencia: this.linha!.nrSequencia,
          cdMedico: this.linha!.medico?.cdMedico ?? null,
          nrMinutoDuracao: this.linha!.nrMinutoDuracao,
        }));
      this.salvo.emit(r.mensagem || (this.encaixe ? 'Encaixe criado com sucesso.' : 'Agendamento criado com sucesso.'));
    } catch (e) {
      this.erro = mensagemErroAgenda(e, this.encaixe ? 'Erro ao criar encaixe.' : 'Erro ao criar agendamento.');
    } finally {
      this.salvando = false;
    }
  }

  cancelar() {
    if (this.salvando) return;
    this.fechar.emit();
  }

  trackCodigo(_: number, o: { codigo: number }) { return o.codigo; }
}
